import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { DashboardLayout, Card, Grid, CardIcon, CardTitle, CardValue, CardDescription, ActionButton } from '../../components/DashboardLayout';
import glassTheme from '../../styles/glassTheme';
import { dashboardApi, AnalystDashboardData } from '../../services/dashboardApi';

const StatCard = styled(Card)`
  background: linear-gradient(135deg, rgba(0, 184, 255, 0.1) 0%, rgba(0, 215, 255, 0.1) 100%);
  border: 1px solid rgba(0, 184, 255, 0.3);
`;

const ChartPlaceholder = styled.div`
  width: 100%;
  height: 200px;
  background: rgba(0, 0, 0, 0.2);
  border: 1px solid ${glassTheme.colors.border};
  border-radius: ${glassTheme.borderRadius.md};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${glassTheme.colors.text.muted};
  margin-top: 1rem;
`;

const BarContainer = styled.div`
  margin-top: 1rem;
`;

const BarItem = styled.div`
  margin-bottom: 0.75rem;
`;

const BarLabel = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.25rem;
  color: ${glassTheme.colors.text.secondary};
  font-size: 0.9rem;
`;

const Bar = styled.div<{ progress: number; color: string }>`
  width: 100%;
  height: 24px;
  background: rgba(0, 0, 0, 0.3);
  border-radius: ${glassTheme.borderRadius.md};
  overflow: hidden;

  &::after {
    content: '';
    display: block;
    width: ${props => props.progress}%;
    height: 100%;
    background: ${props => props.color};
    box-shadow: 0 0 10px ${props => props.color};
  }
`;

const MetricCard = styled(Card)`
  text-align: center;
`;

const MetricValue = styled.div<{ trend: 'up' | 'down' | 'stable' }>`
  font-size: 2rem;
  font-weight: bold;
  color: ${props => {
    switch (props.trend) {
      case 'up': return '#00FF88';
      case 'down': return '#FF4444';
      case 'stable': return glassTheme.colors.text.primary;
      default: return glassTheme.colors.text.primary;
    }
  }};
  margin-bottom: 0.5rem;
`;

const MetricTrend = styled.div<{ trend: 'up' | 'down' | 'stable' }>`
  font-size: 0.85rem;
  color: ${props => {
    switch (props.trend) {
      case 'up': return '#00FF88';
      case 'down': return '#FF4444';
      case 'stable': return glassTheme.colors.text.muted;
      default: return glassTheme.colors.text.muted;
    }
  }};
`;

export const AnalystDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [data, setData] = useState<AnalystDashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        setError(null);
        setData(await dashboardApi.getAnalyst());
      } catch (err) {
        console.error('Failed to load analyst dashboard:', err);
        setError('无法加载分析数据，请稍后重试');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const PALETTE = ['#00B8FF', '#FF69B4', '#00FF88', '#FFB800', '#FF4444', '#888888'];
  const toSeries = (rows: Array<{ key: string; count: number }> | undefined, labels?: Record<string, string>) =>
    (rows || []).map((r, i) => ({
      label: labels && labels[r.key] ? labels[r.key] : (r.key === 'unknown' ? '未填写' : r.key),
      value: r.count,
      color: PALETTE[i % PALETTE.length]
    }));

  const demographics = toSeries(data?.demographics.gender, { male: '男性', female: '女性', 男: '男性', 女: '女性' });
  const statsLevels = toSeries(data?.demographics.statsLevel, {
    none: '无基础', beginner: '初级', basic: '基础', intermediate: '中级', advanced: '高级'
  });
  const accountTypes = toSeries(data?.demographics.statsBackground, {
    true: '有统计学基础', false: '无统计学基础', unknown: '未填写'
  });

  const collegeTotal = (data?.demographics.college || []).reduce((s, c) => s + c.count, 0) || 1;
  const topColleges = (data?.demographics.college || []).slice(0, 5).map(c => ({
    name: c.key,
    count: c.count,
    percentage: Math.round((c.count / collegeTotal) * 100)
  }));

  const consent = data?.consent;
  const metrics: Array<{
    label: string;
    value: string;
    trend: 'up' | 'down' | 'stable';
    change: string;
  }> = [
    { label: '学生总数', value: consent ? String(consent.totalStudents) : '—', trend: 'stable', change: '' },
    { label: '已授权研究使用', value: consent ? String(consent.granted) : '—', trend: 'stable', change: '' },
    { label: '授权覆盖率', value: consent ? `${consent.coverageRate}%` : '—', trend: 'stable', change: '' },
    { label: '未记录授权', value: consent ? String(consent.notRecorded) : '—', trend: 'stable', change: '' }
  ];

  const quickActions = [
    {
      icon: '📊',
      title: '数据分析',
      description: '深入分析平台数据',
      onClick: () => navigate('/analytics/reports')
    },
    {
      icon: '📥',
      title: '数据导出',
      description: consent && consent.researchReady ? '导出研究数据' : '需先取得全部研究授权',
      onClick: () => navigate('/analytics/export')
    },
    {
      icon: '📈',
      title: '自定义报告',
      description: '生成自定义报告',
      onClick: () => navigate('/analytics/custom')
    },
    {
      icon: '🔍',
      title: '趋势分析',
      description: '查看数据趋势',
      onClick: () => navigate('/analytics/trends')
    }
  ];

  return (
    <DashboardLayout
      title="数据分析仪表盘"
      subtitle="平台数据洞察与分析"
    >
      {error && (
        <Card style={{ marginBottom: '1.5rem' }}>
          <CardIcon>⚠️</CardIcon>
          <CardDescription>{error}</CardDescription>
        </Card>
      )}

      {/* Research use is gated on consent. Surfaced first, deliberately:
          analysing student data without recorded consent is an ethics failure,
          not a missing feature. */}
      {consent && (
        <Card
          style={{
            marginBottom: '1.5rem',
            borderColor: consent.researchReady
              ? `${glassTheme.colors.status.success}55`
              : `${glassTheme.colors.status.warning}55`
          }}
        >
          <CardIcon>{consent.researchReady ? '✅' : '⚠️'}</CardIcon>
          <CardTitle>
            研究数据使用授权：{consent.granted} / {consent.totalStudents}（{consent.coverageRate}%）
          </CardTitle>
          <CardDescription>
            {consent.researchReady
              ? '全部学生已记录研究使用授权，数据可用于研究分析。'
              : `尚有 ${consent.notRecorded} 名学生未记录授权、${consent.declined} 名已拒绝。未取得授权的数据不得用于研究分析或对外导出。`}
          </CardDescription>
        </Card>
      )}

      <Grid>
        {metrics.map((metric, index) => (
          <MetricCard key={index}>
            <CardIcon>📊</CardIcon>
            <MetricValue trend={metric.trend}>{loading ? '...' : metric.value}</MetricValue>
            <CardTitle>{metric.label}</CardTitle>
            <MetricTrend trend={metric.trend}>
              {metric.change || '当前值'}
            </MetricTrend>
          </MetricCard>
        ))}

        {quickActions.map((action, index) => (
          <Card key={index} onClick={action.onClick} style={{ cursor: 'pointer' }}>
            <CardIcon>{action.icon}</CardIcon>
            <CardTitle>{action.title}</CardTitle>
            <CardDescription>{action.description}</CardDescription>
          </Card>
        ))}
      </Grid>

      <Grid>
        <Card>
          <CardTitle>性别分布</CardTitle>
          <BarContainer>
            {demographics.map((item) => (
              <BarItem key={item.label}>
                <BarLabel>
                  <span>{item.label}</span>
                  <span>{item.value}%</span>
                </BarLabel>
                <Bar progress={item.value} color={item.color} />
              </BarItem>
            ))}
          </BarContainer>
        </Card>

        <Card>
          <CardTitle>统计学知识水平</CardTitle>
          <BarContainer>
            {statsLevels.map((item) => (
              <BarItem key={item.label}>
                <BarLabel>
                  <span>{item.label}</span>
                  <span>{item.value}%</span>
                </BarLabel>
                <Bar progress={item.value} color={item.color} />
              </BarItem>
            ))}
          </BarContainer>
        </Card>
      </Grid>

      <Grid>
        <Card>
          <CardTitle>账户类型分布</CardTitle>
          <BarContainer>
            {accountTypes.map((item) => (
              <BarItem key={item.label}>
                <BarLabel>
                  <span>{item.label}</span>
                  <span>{item.value}%</span>
                </BarLabel>
                <Bar progress={item.value} color={item.color} />
              </BarItem>
            ))}
          </BarContainer>
        </Card>
      </Grid>

      <Card style={{ marginBottom: '2rem' }}>
        <CardTitle>学院分布 (Top 5)</CardTitle>
        <BarContainer>
          {topColleges.map((college) => (
            <BarItem key={college.name}>
              <BarLabel>
                <span>{college.name}</span>
                <span>{college.count}人 ({college.percentage}%)</span>
              </BarLabel>
              <Bar progress={college.percentage} color="#00B8FF" />
            </BarItem>
          ))}
        </BarContainer>
      </Card>

      <Card>
        <CardTitle>数据可视化</CardTitle>
        <Grid>
          <div>
            <h4 style={{ marginBottom: '1rem', color: glassTheme.colors.text.secondary }}>用户增长趋势</h4>
            <ChartPlaceholder>
              📈 用户增长图表（待集成图表库）
            </ChartPlaceholder>
          </div>
          <div>
            <h4 style={{ marginBottom: '1rem', color: glassTheme.colors.text.secondary }}>学习活跃度</h4>
            <ChartPlaceholder>
              📊 学习活跃度图表（待集成图表库）
            </ChartPlaceholder>
          </div>
        </Grid>
      </Card>
    </DashboardLayout>
  );
};
